

/**
 * Capitalizes the first character of a string.
 * @param {string} stringValue - The string to capitalize.
 * @param {boolean} trimFirst - Default true. Whether to trim the string before capitalizing.
 * @returns {string} The same string with the first character capitalized.
 */
export const capFirstChar = (stringValue, trimFirst = true) => {
  if (!stringValue) return '';
  if (trimFirst) stringValue = stringValue.trim();
  return stringValue.charAt(0).toUpperCase() + stringValue.slice(1);
};

/**
 * Uses the word count of the specified text to estimate its reading time.
 * @param {string} readableText - The text to estimate the reading time of.
 * @returns {integer} - The estimated reading time in rounded whole minutes.
 */
export const estimateReadingTime = (readableText) => {
  if (!readableText?.trim().length) return '0 mins';

  const wordCount = readableText.trim().split(/\s+/).length;
  const readingTime = Math.ceil(wordCount / 235);

  return readingTime;
}

/**
 * Returns the first paragraph of the given text.
 * If the text is empty or null, returns null.
 * If the text has no line breaks, returns the entire text.
 * 
 * Pass `minLength` to return the first paragraph after the specified minimum length.
 */
export const getFirstParagraph = (text, minLength = 0) => {
  if (!text) return null;

  const firstLineBreakAfterMinLength = text.indexOf('\n', minLength - 1);
  return firstLineBreakAfterMinLength >= 0
    ? text.substring(0, firstLineBreakAfterMinLength)
    : text;
};

/**
 * Returns the count plus the singular or plural label based on the count.
 * If the count is 1, returns the singular label, otherwise returns the plural label.
 * 
 * @param {number} count - Required. The count to determine the label.
 * @param {string} singularLabel - Required. The label to use when the count is 1.
 * @param {string} pluralLabel - Optional. The label to use when the count is not 1.
 *   If not provided, the singular label will be used with an 's' appended.
 * 
 * @returns {string} - The count plus the appropriate label.
 * 
 * @example
 * countLabel(1, 'member') // returns '1 member'
 * countLabel(2, 'member') // returns '2 members'
 * countLabel(37, 'goose', 'geese') // returns '37 geese'
 */
export const countLabel = (count, singularLabel, pluralLabel = null) => {
  if (count === 1) return `1 ${singularLabel}`;
  return `${count} ${pluralLabel || `${singularLabel}s`}`;
};
